import { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Select,
  Typography,
  Stack,
  Alert,
  Collapse,
  CircularProgress,
  LinearProgress
} from '@mui/material';

// third party
import { Formik } from 'formik';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';
import { strengthColor, strengthIndicator } from 'utils/password-strength';
import { postApi } from 'views/services/api';
import { submitHubspotLead } from 'views/services/hubspot';
import { hotelRegisterSchema } from 'schema';
import { getTimezones } from 'utils/timeZone';
import { getCountries, getCountryDialCode, buildInternationalPhone } from 'utils/countries';

// icons
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// ===========================|| FIREBASE - REGISTER - PROFESIONAL ||=========================== //

const FirebaseRegister = ({ ...others }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState(0);
  const [level, setLevel] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorStatus, setErrorStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const changePassword = (value) => {
    const temp = strengthIndicator(value);
    setStrength(temp);
    setLevel(strengthColor(temp));
  };

  return (
    <>
      <Collapse in={errorStatus}>
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          onClose={() => setErrorStatus(false)}
          sx={{
            mb: 3,
            borderRadius: '8px',
            backgroundColor: theme.palette.error.light + '20',
            color: theme.palette.error.dark,
            border: '1px solid',
            borderColor: theme.palette.error.light + '50'
          }}
        >
          {errorMessage}
        </Alert>
      </Collapse>

      <Formik
        initialValues={{
          email: '',
          password: '',
          name: '',
          managerName: '',
          contact: '',
          managerPhone: '',
          address: '',
          country: '',
          currency: 'USD',
          timezone: 'America/Caracas',
          role: 'HotelAdmin'
        }}
        validationSchema={hotelRegisterSchema}
        onSubmit={async (values, { resetForm }) => {
          setIsSubmitting(true);
          setErrorStatus(false);
          try {
            // Combina el prefijo del país con el número local del encargado
            // para dejarlo en formato internacional (lead + Sego IA / WhatsApp).
            const payload = {
              ...values,
              contact: buildInternationalPhone(values.country, values.contact),
              managerPhone: buildInternationalPhone(values.country, values.managerPhone)
            };
            const response = await postApi('api/hotel/register', payload);
            if (response.status === 200) {
              // Captura de lead en HubSpot (no bloqueante: si falla, el
              // registro del hotel ya se completó igualmente).
              submitHubspotLead(payload);
              toast.success('¡Registro exitoso! Revisa tu correo para verificar tu cuenta.', {
                autoClose: 5000
              });
              resetForm();
              setTimeout(() => {
                navigate('/login');
              }, 1500);
            } else {
              setErrorStatus(true);
              setErrorMessage(response.response?.data?.message || 'Error en el registro');
            }
          } catch (err) {
            console.log(err);
            setErrorStatus(true);
            setErrorMessage(err.response?.data?.message || 'Error de conexión con el servidor');
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
          <form noValidate onSubmit={handleSubmit} {...others}>
            <Stack spacing={2}>
              {/* Email */}
              <FormControl fullWidth error={Boolean(touched.email && errors.email)}>
                <InputLabel htmlFor="outlined-adornment-email-register">Correo Electrónico</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-email-register"
                  type="email"
                  value={values.email}
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  label="Correo Electrónico"
                />
                {touched.email && errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
              </FormControl>

              {/* Password */}
              <FormControl fullWidth error={Boolean(touched.password && errors.password)}>
                <InputLabel htmlFor="outlined-adornment-password-register">Contraseña de Acceso</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password-register"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  name="password"
                  onBlur={handleBlur}
                  onChange={(e) => {
                    handleChange(e);
                    changePassword(e.target.value);
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="ver contraseña"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="large"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Contraseña de Acceso"
                />
                {touched.password && errors.password && <FormHelperText error>{errors.password}</FormHelperText>}
              </FormControl>

              {/* Strength Indicator: solo visible cuando el usuario empieza a escribir */}
              {values.password && strength !== 0 && (
                <FormControl fullWidth sx={{ mt: -1 }}>
                  <Box sx={{ mb: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Box style={{ backgroundColor: level?.color }} sx={{ width: 85, height: 8, borderRadius: '7px' }} />
                      </Grid>
                      <Grid item>
                        <Typography variant="subtitle1" fontSize="0.75rem">
                          {level?.label}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </FormControl>
              )}

              <Grid container spacing={2}>
                {/* Hotel Name */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(touched.name && errors.name)}>
                    <InputLabel htmlFor="outlined-adornment-name-register">Nombre del Hotel</InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-name-register"
                      type="text"
                      value={values.name}
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      startAdornment={
                        <InputAdornment position="start">
                          <BusinessOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                        </InputAdornment>
                      }
                      label="Nombre del Hotel"
                    />
                    {touched.name && errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
                  </FormControl>
                </Grid>
                {/* Persona Encargada (lead) */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(touched.managerName && errors.managerName)}>
                    <InputLabel htmlFor="outlined-adornment-managerName-register">Persona Encargada</InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-managerName-register"
                      type="text"
                      value={values.managerName}
                      name="managerName"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      startAdornment={
                        <InputAdornment position="start">
                          <BadgeOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                        </InputAdornment>
                      }
                      label="Persona Encargada"
                    />
                    {touched.managerName && errors.managerName && <FormHelperText error>{errors.managerName}</FormHelperText>}
                  </FormControl>
                </Grid>
              </Grid>

              {/* Country (lead) — va antes de los teléfonos para derivar el prefijo */}
              <FormControl fullWidth error={Boolean(touched.country && errors.country)}>
                <InputLabel htmlFor="outlined-adornment-country-register">País</InputLabel>
                <Select
                  id="outlined-adornment-country-register"
                  native
                  value={values.country}
                  name="country"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  input={
                    <OutlinedInput
                      label="País"
                      startAdornment={
                        <InputAdornment position="start">
                          <PublicOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                        </InputAdornment>
                      }
                    />
                  }
                >
                  <option value="" disabled>
                    Selecciona un país
                  </option>
                  {getCountries().map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
                {touched.country && errors.country && <FormHelperText error>{errors.country}</FormHelperText>}
              </FormControl>

              <Grid container spacing={2}>
                {/* Contact Number (línea del hotel) */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(touched.contact && errors.contact)}>
                    <InputLabel htmlFor="outlined-adornment-contact-register">Teléfono del Hotel</InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-contact-register"
                      type="text"
                      value={values.contact}
                      name="contact"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder={getCountryDialCode(values.country) ? 'Número local' : 'Ej: +58 212...'}
                      startAdornment={
                        <InputAdornment position="start">
                          <PhoneIphoneOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                          {getCountryDialCode(values.country) && (
                            <Typography sx={{ ml: 0.5, fontWeight: 600, color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                              {getCountryDialCode(values.country)}
                            </Typography>
                          )}
                        </InputAdornment>
                      }
                      label="Teléfono del Hotel"
                    />
                    {touched.contact && errors.contact && <FormHelperText error>{errors.contact}</FormHelperText>}
                  </FormControl>
                </Grid>
                {/* Teléfono del Encargado (lead + Sego IA). El prefijo se deriva del país. */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(touched.managerPhone && errors.managerPhone)}>
                    <InputLabel htmlFor="outlined-adornment-managerPhone-register">Teléfono del Encargado</InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-managerPhone-register"
                      type="text"
                      value={values.managerPhone}
                      name="managerPhone"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder={getCountryDialCode(values.country) ? 'Número local' : 'Ej: +58 414...'}
                      startAdornment={
                        <InputAdornment position="start">
                          <PhoneIphoneOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                          {getCountryDialCode(values.country) && (
                            <Typography sx={{ ml: 0.5, fontWeight: 600, color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                              {getCountryDialCode(values.country)}
                            </Typography>
                          )}
                        </InputAdornment>
                      }
                      label="Teléfono del Encargado"
                    />
                    {touched.managerPhone && errors.managerPhone ? (
                      <FormHelperText error>{errors.managerPhone}</FormHelperText>
                    ) : (
                      <FormHelperText>
                        {getCountryDialCode(values.country)
                          ? 'Escribe solo tu número; el prefijo se agrega automáticamente.'
                          : 'Incluye el código del país (ej. +58).'}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {/* Address */}
              <FormControl fullWidth error={Boolean(touched.address && errors.address)}>
                <InputLabel htmlFor="outlined-adornment-address-register">Dirección Física</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-address-register"
                  type="text"
                  value={values.address}
                  name="address"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  label="Dirección Física"
                />
                {touched.address && errors.address && <FormHelperText error>{errors.address}</FormHelperText>}
              </FormControl>

              <Grid container spacing={2}>
                {/* Currency */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(touched.currency && errors.currency)}>
                    <InputLabel htmlFor="outlined-adornment-currency-register">Moneda Principal</InputLabel>
                    <Select
                      id="outlined-adornment-currency-register"
                      native
                      value={values.currency}
                      name="currency"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      input={
                        <OutlinedInput
                          label="Moneda Principal"
                          startAdornment={
                            <InputAdornment position="start">
                              <MonetizationOnOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                            </InputAdornment>
                          }
                        />
                      }
                    >
                      <option value="USD">USD - Dólar estadounidense</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="MXN">MXN - Peso mexicano</option>
                      <option value="ARS">ARS - Peso argentino</option>
                      <option value="BOB">BOB - Boliviano</option>
                      <option value="BRL">BRL - Real brasileño</option>
                      <option value="CLP">CLP - Peso chileno</option>
                      <option value="COP">COP - Peso colombiano</option>
                      <option value="CRC">CRC - Colón costarricense</option>
                      <option value="CUP">CUP - Peso cubano</option>
                      <option value="DOP">DOP - Peso dominicano</option>
                      <option value="GTQ">GTQ - Quetzal guatemalteco</option>
                      <option value="HNL">HNL - Lempira hondureño</option>
                      <option value="NIO">NIO - Córdoba nicaragüense</option>
                      <option value="PAB">PAB - Balboa panameño</option>
                      <option value="PEN">PEN - Sol peruano</option>
                      <option value="PYG">PYG - Guaraní paraguayo</option>
                      <option value="UYU">UYU - Peso uruguayo</option>
                      <option value="VES">VES - Bolívar venezolano</option>
                    </Select>
                    {touched.currency && errors.currency && <FormHelperText error>{errors.currency}</FormHelperText>}
                  </FormControl>
                </Grid>
                {/* Timezone */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(touched.timezone && errors.timezone)}>
                    <InputLabel htmlFor="outlined-adornment-timezone-register">Zona Horaria</InputLabel>
                    <Select
                      id="outlined-adornment-timezone-register"
                      native
                      value={values.timezone}
                      name="timezone"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      input={
                        <OutlinedInput
                          label="Zona Horaria"
                          startAdornment={
                            <InputAdornment position="start">
                              <LanguageOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                            </InputAdornment>
                          }
                        />
                      }
                    >
                      {getTimezones().map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </Select>
                    {touched.timezone && errors.timezone && <FormHelperText error>{errors.timezone}</FormHelperText>}
                  </FormControl>
                </Grid>
              </Grid>

              {isSubmitting && (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
                </Box>
              )}

              {/* Submit Button */}
              <Box sx={{ mt: 2 }}>
                <AnimateButton>
                  <Button
                    disableElevation
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      py: 1.5,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                        <span>Procesando Registro...</span>
                      </Stack>
                    ) : (
                      'Crear Cuenta de Hotel'
                    )}
                  </Button>
                </AnimateButton>
              </Box>
            </Stack>
          </form>
        )}
      </Formik>
    </>
  );
};

export default FirebaseRegister;
